import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

function SortIcon({ dir }) {
  if (!dir) return <span className="ml-1 opacity-30">↕</span>;
  return <span className="ml-1">{dir === "asc" ? "↑" : "↓"}</span>;
}

const CATEGORY_COLORS = [
  "bg-blue-900/40 text-blue-300 border-blue-700",
  "bg-purple-900/40 text-purple-300 border-purple-700",
  "bg-emerald-900/40 text-emerald-300 border-emerald-700",
  "bg-amber-900/40 text-amber-300 border-amber-700",
  "bg-rose-900/40 text-rose-300 border-rose-700",
  "bg-cyan-900/40 text-cyan-300 border-cyan-700",
  "bg-indigo-900/40 text-indigo-300 border-indigo-700",
  "bg-orange-900/40 text-orange-300 border-orange-700",
];

function categoryColor(cat, allCats) {
  if (!cat) return "bg-muted text-muted-foreground border-border";
  const idx = allCats.indexOf(cat);
  return CATEGORY_COLORS[idx % CATEGORY_COLORS.length];
}

export default function Dashboard({ user, onLogout }) {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState(null);
  const [sort, setSort] = useState({ col: "category", dir: "asc" });

  const fetchItems = async () => {
    const res = await api.get(`/discounts`);
    setItems(res.data || []);
  };

  useEffect(() => { fetchItems(); }, []);

  const logout = async () => {
    await api.post(`/auth/logout`, {});
    onLogout();
  };

  const categories = useMemo(() => {
    const cats = [...new Set(items.map(i => i.category).filter(Boolean))].sort((a, b) => a.localeCompare(b));
    return cats;
  }, [items]);

  const filtered = useMemo(() => {
    let list = items;
    if (activeCategory) list = list.filter(i => i.category === activeCategory);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(i =>
        (i.name || "").toLowerCase().includes(q) ||
        (i.category || "").toLowerCase().includes(q) ||
        (i.discount || "").toLowerCase().includes(q) ||
        (i.notes || "").toLowerCase().includes(q)
      );
    }
    return [...list].sort((a, b) => {
      const { col, dir } = sort;
      const av = (a[col] || "").toLowerCase();
      const bv = (b[col] || "").toLowerCase();
      const cmp = av.localeCompare(bv);
      return dir === "asc" ? cmp : -cmp;
    });
  }, [items, search, activeCategory, sort]);

  const toggleSort = (col) => {
    setSort(prev => prev.col === col
      ? { col, dir: prev.dir === "asc" ? "desc" : "asc" }
      : { col, dir: "asc" }
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-10 border-b border-border/60 bg-background/80 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-xl font-bold tracking-tight bg-gradient-to-r from-primary to-cyan-400 bg-clip-text text-transparent">
              Member Discounts
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground" data-testid="user-greeting">
              Hi, {user?.name || user?.email || "user"}
            </span>
            {user?.is_admin && (
              <a href="/admin" data-testid="go-admin-link" className="text-sm text-primary hover:underline font-medium">
                Admin
              </a>
            )}
            <Button variant="outline" size="sm" data-testid="logout-button" onClick={logout}>
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        {/* Search + stats row */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <h1 className="text-2xl font-semibold" data-testid="dashboard-heading">Available Discounts</h1>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Input
              data-testid="discount-search-input"
              placeholder="Search discounts…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full sm:w-64"
            />
            {search && (
              <Button variant="ghost" size="sm" onClick={() => setSearch("")}>✕</Button>
            )}
          </div>
        </div>

        {/* Category filter pills */}
        {categories.length > 0 && (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setActiveCategory(null)}
              className={`px-3 py-1 rounded-full text-sm font-medium border transition-colors ${
                activeCategory === null
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-muted text-muted-foreground border-border hover:border-primary/50"
              }`}
            >
              All
            </button>
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(activeCategory === cat ? null : cat)}
                className={`px-3 py-1 rounded-full text-sm font-medium border transition-colors ${
                  activeCategory === cat
                    ? `${categoryColor(cat, categories)} border-opacity-100`
                    : `${categoryColor(cat, categories)} opacity-60 hover:opacity-100`
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        )}

        {/* Results count */}
        <p className="text-sm text-muted-foreground">
          {filtered.length === items.length
            ? `${items.length} discount${items.length !== 1 ? "s" : ""}`
            : `${filtered.length} of ${items.length} discounts`}
        </p>

        {/* Table */}
        <Card className="border-border/60">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-border/60 hover:bg-transparent">
                    {[
                      { col: "category", label: "Category" },
                      { col: "name", label: "Name" },
                      { col: "discount", label: "Discount" },
                    ].map(({ col, label }) => (
                      <TableHead
                        key={col}
                        className="cursor-pointer select-none hover:text-foreground transition-colors"
                        onClick={() => toggleSort(col)}
                      >
                        {label}
                        <SortIcon dir={sort.col === col ? sort.dir : null} />
                      </TableHead>
                    ))}
                    <TableHead>Link</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((it) => (
                    <TableRow key={it.id} data-testid={`discount-row-${it.id}`} className="border-border/40 hover:bg-muted/30 transition-colors">
                      <TableCell>
                        {it.category
                          ? <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border ${categoryColor(it.category, categories)}`}>{it.category}</span>
                          : <span className="text-muted-foreground">—</span>
                        }
                      </TableCell>
                      <TableCell className="font-medium">{it.name || <span className="text-muted-foreground">—</span>}</TableCell>
                      <TableCell>
                        {it.discount
                          ? <span className="text-emerald-400 font-semibold">{it.discount}</span>
                          : <span className="text-muted-foreground">—</span>
                        }
                      </TableCell>
                      <TableCell>
                        {it.link
                          ? <a data-testid={`discount-link-${it.id}`} className="text-primary hover:text-primary/80 underline underline-offset-2 text-sm" href={it.link} target="_blank" rel="noreferrer">Visit →</a>
                          : <span className="text-muted-foreground">—</span>
                        }
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-normal">{it.notes || <span>—</span>}</TableCell>
                    </TableRow>
                  ))}
                  {filtered.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-12 text-muted-foreground" data-testid="empty-discounts">
                        {items.length === 0 ? "No discounts yet" : "No discounts match your search"}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
